import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ApiResult, getXSRFToken } from "../../../../../api/v2/ApiUtils";
import { createStep, deleteStep, updateStep } from "../../../../../api/v2/StepsApi";
import { getPhaseById, getSteps } from "../../../../../api/v2/PhasesApi";

import { PermissionType } from "../../../../../dtos/PermissionType";
import { PhaseDto } from "../../../../../dtos/PhaseDto";
import { PhaseStatus } from "../../../../../dtos/PhaseStatus";
import { PhaseWorkflowType } from "../../../../../dtos/PhaseWorkflowType";
import { StepDto } from "../../../../../dtos/StepDto";
import { StepStatus } from "../../../../../dtos/StepStatus";
import { StepWorkflowType } from "../../../../../dtos/StepWorkflowType";
import { TrackedMode } from "../../../../../dtos/TrackedMode";

import useAsync from "../../../../../hooks/useAsync";
import { useAppContext } from "../../../AppContext";
import { getStepStatusInfo, getStepStatusInfoByWorkflow, resolveTasksFlags, checkStepPermission, checkPhasePermission, canViewResponseContent, getEffectiveStepType, stepToPhaseWorkflowType, isHybridParentType, isUploadOnlyPhase, trimStepText } from "../../../Utils";

import { confirm } from 'devextreme/ui/dialog';
import { TasksFeatureFlags } from "../../../Types";
import { useTaskListActions, useTaskListSelectionState } from "../../context/TaskListContext";
import { ITasksProps } from "../Tasks";
import { ITasksViewProps } from "../components/TasksView";
import { mapDefaultStepToStepDto, mapStepDtoToDefaultStep } from "../adapters/templateMappers";
import { createDefaultSubtask, updateDefaultSubtask } from "../../../../../api/v2/TasklistsApi";
import { DefaultStepDto } from "../../../../../dtos/DefaultStepDto";
import { isClosedCycle } from "../taskCycle";

export function useTasksController({
    grantData,
    selectedStep: propsSelectedStep,
    selectedPhase: propsSelectedPhase,
    phasesInfo,
    deepLinkHash,
    featureOverrides,
    requestedSummaryPhase,
    nameShownExternally,
    externalPhase
}: ITasksProps): ITasksViewProps {
    const app = useAppContext();
    const taskListActions = useTaskListActions();
    const { tasklistMode, selectedStep: contextSelectedStep, selectedPhase: contextSelectedPhase, selectedSubmissionId } = useTaskListSelectionState();

    // A prior cycle keeps its subtasks as they are: the server rejects edits and status changes
    // to it (files and comments stay open), so those controls must not be offered at all.
    const isCycleClosed = tasklistMode !== "templates"
        && isClosedCycle(contextSelectedPhase, selectedSubmissionId);

    // Read from inside the memoized editor options and the disabled callbacks. A ref keeps the
    // value current without adding a dependency to each of them.
    const cycleClosedRef = useRef(isCycleClosed);
    cycleClosedRef.current = isCycleClosed;

    // Read mode from context - this will cause re-renders when mode changes,
    // but mode changes are infrequent (only when switching between grant/template views)
    const mode = tasklistMode;

    // IMPORTANT: use selectedStep/selectedPhase from CONTEXT, not props
    // props may be stale because Tasks component is memoized
    const selectedStep = contextSelectedStep;
    const selectedPhase = contextSelectedPhase;

    // Get workflow type from the selected phase or default phase.
    // Under a hybrid parent task (PhaseHybrid or StepHybrid), when a subtask is selected,
    // resolve to its per-subtask effective type so the detail panel layout (None vs.
    // phase-style) and the rest of the controller's branching pick the right path.
    const parentWorkflowType = selectedPhase?.defaultPhase?.settings?.workflowType ?? null;
    const isHybridParent = isHybridParentType(parentWorkflowType);
    const workflowType = (isHybridParent && selectedStep?.defaultStep && selectedPhase?.defaultPhase)
        ? stepToPhaseWorkflowType(getEffectiveStepType(selectedPhase.defaultPhase, selectedStep.defaultStep))
        : parentWorkflowType;

    // Subtasks of an upload-only task are document folders: files only, nothing else to edit.
    const uploadOnly = isUploadOnlyPhase(selectedPhase);

    const flags = useMemo<TasksFeatureFlags>(() => resolveTasksFlags(mode, featureOverrides, workflowType), [mode, featureOverrides, workflowType]);

    const showLoadRef = useRef<() => void>(() => { });
    const hideLoadRef = useRef<() => void>(() => { });
    const registerLoadPanel = useCallback((showFn: () => void, hideFn: () => void) => {
        showLoadRef.current = showFn;
        hideLoadRef.current = hideFn;
    }, []);

    // The panel waits this long before painting (its own delay option), so a load that finishes
    // inside the window never shows one at all.
    const LOAD_PANEL_DELAY_MS = 300;
    // Once it has painted it stays this long. A panel that comes and goes inside a few hundred
    // milliseconds reads as a flash; the content loads in behind it either way.
    const LOAD_PANEL_MIN_VISIBLE_MS = 1200;

    const loadPanelShownAt = useRef<number | null>(null);
    const loadPanelHideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Skip the scoped tasklist spinner while the global load panel is up, so the two don't stack as
    // competing overlays. Hiding is always allowed (harmless if it was never shown).
    const showTaskListLoadPanel = useCallback(() => {
        if (app.isLoadPanelVisible?.()) return;
        if (loadPanelHideTimer.current) {
            clearTimeout(loadPanelHideTimer.current);
            loadPanelHideTimer.current = null;
        }
        if (loadPanelShownAt.current === null) loadPanelShownAt.current = Date.now();
        showLoadRef.current();
    }, [app]);

    const hideTaskListLoadPanel = useCallback(() => {
        const hideNow = () => {
            loadPanelHideTimer.current = null;
            loadPanelShownAt.current = null;
            hideLoadRef.current();
        };

        const shownAt = loadPanelShownAt.current;
        if (shownAt === null) {
            hideNow();
            return;
        }

        // Still inside the paint delay: nothing is on screen, so drop it rather than let it appear.
        const elapsed = Date.now() - shownAt;
        if (elapsed < LOAD_PANEL_DELAY_MS) {
            hideNow();
            return;
        }

        const remaining = LOAD_PANEL_DELAY_MS + LOAD_PANEL_MIN_VISIBLE_MS - elapsed;
        if (remaining <= 0) {
            hideNow();
            return;
        }
        if (loadPanelHideTimer.current) return;
        loadPanelHideTimer.current = setTimeout(hideNow, remaining);
    }, []);

    useEffect(() => () => {
        if (loadPanelHideTimer.current) clearTimeout(loadPanelHideTimer.current);
    }, []);

    const xsrf = useAsync<string | null>(() => getXSRFToken(), []);

    const inTemplateMode = mode === "templates";

    const [showSummary, setShowSummary] = useState(false);
    const [selectedSummaryPhase, setSelectedSummaryPhase] = useState<PhaseDto | null>(null);
    const [initialLoad, setInitialLoad] = useState<boolean>(false);
    const [loadingTasks, setLoadingTasks] = useState<number>(0);

    // Track visibility of each task by index
    const [taskVisibility, setTaskVisibility] = useState<Map<number, boolean>>(new Map());
    const hasAnyVisibleTasks = useMemo(() => {
        // If no phases exist, there are no visible tasks
        if (!phasesInfo || phasesInfo.length === 0) return false;
        // If visibility hasn't been reported yet, assume visible while loading
        if (taskVisibility.size === 0) return true;
        return Array.from(taskVisibility.values()).some(v => v);
    }, [taskVisibility, phasesInfo]);

    // A collapsed task shows its toolbar and nothing else, so once every task in the box is
    // collapsed the box has no content to stand a minimum height for - it closes up to the
    // toolbars, the way a DC task with the extended header closes up to its header row.
    const [taskCollapsed, setTaskCollapsed] = useState<Map<number, boolean>>(new Map());

    const everyTaskCollapsed = useMemo(() => {
        if (phasesInfo.length === 0) return false;
        return phasesInfo.every((_, idx) => taskCollapsed.get(idx) === true);
    }, [taskCollapsed, phasesInfo]);

    const handleTaskCollapsedChange = useCallback((index: number, collapsed: boolean) => {
        setTaskCollapsed(prev => {
            if (prev.get(index) === collapsed) return prev;
            const next = new Map(prev);
            next.set(index, collapsed);
            return next;
        });
    }, []);

    const handleTaskVisibilityChange = useCallback((index: number, visible: boolean) => {
        setTaskVisibility(prev => {
            // Only update if the value actually changed to avoid re-render loops
            if (prev.get(index) === visible) {
                return prev; // Return same reference to prevent re-render
            }
            const next = new Map(prev);
            next.set(index, visible);
            return next;
        });
    }, []);

    const readyDisabled = useRef<boolean>(true);
    const completeDisabled = useRef<boolean>(true);

    // Sibling steps for date overlap constraints
    const [siblingSteps, setSiblingSteps] = useState<StepDto[]>([]);
    const trackedMode = selectedPhase?.defaultPhase?.settings?.trackedMode;
    const isNoOverlapMode = trackedMode === TrackedMode.NoOverlap;
    const isEndDateOnlyMode = trackedMode === TrackedMode.EndDateOnly;

    // Load sibling steps when phase changes and NoOverlap mode is enabled
    useEffect(() => {
        if (!selectedPhase || !isNoOverlapMode || inTemplateMode) {
            setSiblingSteps([]);
            return;
        }

        const loadSteps = async () => {
            const subId = selectedPhase.phaseSubmissions?.[0]?.id;
            if (!subId) {
                setSiblingSteps([]);
                return;
            }
            const result = await getSteps(subId);
            if (result?.data) {
                // Sort by order
                const sorted = [...result.data].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
                setSiblingSteps(sorted);
            }
        };

        loadSteps();
    }, [selectedPhase?.id, selectedPhase?.phaseSubmissions, isNoOverlapMode, inTemplateMode]);

    // Track current form date values for real-time min/max constraints
    // Also track which step these values belong to, to avoid stale state from previous step
    const [formDates, setFormDates] = useState<{
        stepId: string | null;
        submissionDate: Date | null;
        completionDate: Date | null;
    }>({ stepId: null, submissionDate: null, completionDate: null });

    // Sync form date state when selectedStep changes
    useEffect(() => {
        setFormDates({
            stepId: selectedStep?.id ?? null,
            submissionDate: selectedStep?.submissionDate ? new Date(selectedStep.submissionDate) : null,
            completionDate: selectedStep?.completionDate ? new Date(selectedStep.completionDate) : null
        });
    }, [selectedStep?.id, selectedStep?.submissionDate, selectedStep?.completionDate]);

    // Handle form field changes for real-time date constraint updates
    const onFieldDataChanged = useCallback((e: { dataField?: string; value?: any }) => {
        if (e.dataField === 'submissionDate' || e.dataField === 'completionDate') {
            setFormDates(prev => {
                // Only update if this is for the current step
                if (prev.stepId !== selectedStep?.id) return prev;
                return {
                    ...prev,
                    [e.dataField as string]: e.value ? new Date(e.value) : null
                };
            });
        }
    }, [selectedStep?.id]);

    // Get current dates, but only if they belong to the current step
    const currentSubmissionDate = formDates.stepId === selectedStep?.id ? formDates.submissionDate : null;
    const currentCompletionDate = formDates.stepId === selectedStep?.id ? formDates.completionDate : null;

    const [htmlEditorExpanded, setHtmlEditorExpanded] = useState<boolean>(false);
    const htmlEditorText = useRef<string | null>(null);
    const htmlEditorTarget = useRef<"request" | "response" | null>(null);

    // Compute permissions from workflow config (no API calls needed)
    // Pass selectedPhase for fallback to phase's DefaultStepSettings (matches backend behavior)
    // Pass grantData.id for resolving external user roles to their grant-specific permissions
    const hasEditPermission = useMemo(() => {
        if (!selectedStep || inTemplateMode) return false;
        return checkStepPermission(selectedStep, app, PermissionType.Edit, true, selectedPhase, grantData?.id);
    }, [selectedStep, app, inTemplateMode, selectedPhase, grantData]);

    const hasEditDatesPermission = useMemo(() => {
        if (!selectedStep || inTemplateMode) return false;
        return checkStepPermission(selectedStep, app, PermissionType.EditDates, true, selectedPhase, grantData?.id);
    }, [selectedStep, app, inTemplateMode, selectedPhase, grantData]);

    const hasUploadPermission = useMemo(() => {
        if (!selectedStep || inTemplateMode) return false;
        return checkStepPermission(selectedStep, app, PermissionType.Upload, true, selectedPhase, grantData?.id);
    }, [selectedStep, app, inTemplateMode, selectedPhase, grantData]);

    const hasDeletePermission = useMemo(() => {
        if (!selectedStep || inTemplateMode) return false;
        return checkStepPermission(selectedStep, app, PermissionType.Delete, true, selectedPhase, grantData?.id);
    }, [selectedStep, app, inTemplateMode, selectedPhase, grantData]);

    const hasEditResponsePermission = useMemo(() => {
        if (!selectedStep || inTemplateMode) return false;
        return checkStepPermission(selectedStep, app, PermissionType.EditResponse, true, selectedPhase, grantData?.id);
    }, [selectedStep, app, inTemplateMode, selectedPhase, grantData]);

    const hasEditRequestPermission = useMemo(() => {
        if (!selectedStep || inTemplateMode) return false;
        return checkStepPermission(selectedStep, app, PermissionType.EditRequest, true, selectedPhase, grantData?.id);
    }, [selectedStep, app, inTemplateMode, selectedPhase, grantData]);

    const hasViewPermission = useMemo(() => {
        if (!selectedStep || inTemplateMode) return true; // Templates always allow viewing
        return checkStepPermission(selectedStep, app, PermissionType.View, true, selectedPhase, grantData?.id);
    }, [selectedStep, app, inTemplateMode, selectedPhase, grantData]);

    // Computed value: can the user edit anything at all?
    // Used to determine if Save button should be visible
    // Note: Upload permission doesn't count - it's for file uploads, not form field edits
    const canEditAnything = useMemo(() => {
        if (inTemplateMode) return true; // Templates always allow editing
        if (!selectedStep) return false;

        // Nothing on a closed cycle can be saved, whatever editing permissions the user holds.
        if (isCycleClosed) return false;

        // Upload-only subtasks have no editable fields, so there is nothing to "Save".
        if (uploadOnly) return false;

        // For new subtasks (no ID yet), saving is always allowed
        if (!selectedStep.id) {
            return true;
        }

        // For None workflow, check Edit and EditDates permissions (not Upload - that's for files only)
        if (workflowType === PhaseWorkflowType.None) {
            return hasEditPermission || hasEditDatesPermission;
        }

        // For Phase/Step workflows, check request/response edit permissions
        return hasEditRequestPermission || hasEditResponsePermission || hasEditDatesPermission;
    }, [inTemplateMode, selectedStep, workflowType, hasEditPermission, hasEditDatesPermission, hasEditRequestPermission, hasEditResponsePermission]);

    useEffect(() => {
        taskListActions.setTasklistMode(mode);
        if (phasesInfo && phasesInfo.length > 0) {
            showTaskListLoadPanel();
            setInitialLoad(true);
        }
    }, [phasesInfo, mode, taskListActions]);

    useEffect(() => {
        if (initialLoad) {
            if (loadingTasks === 1) {
                showTaskListLoadPanel();
            } else if (loadingTasks === 0) {
                hideTaskListLoadPanel();
                setInitialLoad(false);
            }
            return;
        }

        // Every later load gets the same panel. Tasks fetch their subtasks again whenever the list
        // refreshes - a status change, an upload, a comment, switching cycle - and until this ran
        // outside the initial load those waits showed nothing at all. The panel's own 300ms delay
        // keeps a quick refresh from flashing it.
        if (loadingTasks > 0) {
            showTaskListLoadPanel();
        } else {
            hideTaskListLoadPanel();
        }
    }, [initialLoad, loadingTasks, showTaskListLoadPanel, hideTaskListLoadPanel]);

    // keep buttons enabled states in sync with comment text
    useEffect(() => {
        const getPlainText = (html: string | undefined | null): string =>
            (html ?? "").replace(/<[^>]+>/g, "").trim();

        const requestCommentText = getPlainText(selectedStep?.requestComment?.comment);
        readyDisabled.current = !requestCommentText;

        // For None workflow, require description text
        // For other workflows, require response comment text
        if (workflowType === PhaseWorkflowType.None) {
            const descriptionText = (selectedStep?.description ?? "").trim();
            completeDisabled.current = !descriptionText;
        } else {
            const responseCommentText = getPlainText(selectedStep?.responseComment?.comment);
            completeDisabled.current = !responseCommentText;
        }
    }, [selectedStep, workflowType]);

    // Save (create/update)
    const onClickSave = useCallback(async (skipRefresh = false) => {

        if (!selectedStep) return;
        let step: StepDto | null = null;

        // Drop leading/trailing whitespace the user typed into the description and the
        // request/response bodies so it is not stored.
        let stepToSave = trimStepText(selectedStep);

        // The form declares a RequiredRule on the name, but nothing validates the form on save,
        // so the rule alone never stopped an empty one being stored. Say why rather than failing
        // silently; the server refuses it too.
        if (!stepToSave.name?.trim()) {
            app.errorToast("A subtask needs a name.");
            return;
        }

        // Auto-set start date when completion date is set but start date is blank
        if (stepToSave.completionDate && !stepToSave.submissionDate) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            stepToSave = {
                ...stepToSave,
                submissionDate: today
            };
        }

        if (mode === "templates") {
            const defaultStepDto = mapStepDtoToDefaultStep(stepToSave);

            if (defaultStepDto.id != 0) {
                const result = await updateDefaultSubtask(selectedPhase?.defaultPhase.moduleTypeId as number, defaultStepDto);
                if (result?.error) app.errorToast(result.error.message);
                step = mapDefaultStepToStepDto(result?.data as DefaultStepDto) ?? null;
            } else {
                const result = await createDefaultSubtask(selectedPhase?.defaultPhase.moduleTypeId as number, defaultStepDto);
                if (result?.error) app.errorToast(result.error.message);
                step = mapDefaultStepToStepDto(result?.data as DefaultStepDto) ?? null;
            }
        } else {
            if (stepToSave.id) {
                const result = await updateStep(stepToSave.id, stepToSave);
                if (result?.error) app.errorToast(result.error.message);
                step = (result?.data as StepDto) ?? null;
            } else {
                const result = await createStep(stepToSave);
                if (result?.error) app.errorToast(result.error.message);
                step = (result?.data as StepDto) ?? null;
            }
        }

        if (step && !skipRefresh) await refreshStepChanges(step);
        hideTaskListLoadPanel();
    }, [selectedStep, app, hideTaskListLoadPanel, mode, selectedPhase]);

    const refreshStepChanges = useCallback(
        async (step: StepDto) => {
            showTaskListLoadPanel();

            // Check if user will still have View permission for the step at its new status BEFORE updating selection
            const canStillViewStep = checkStepPermission(step, app, PermissionType.View, true, selectedPhase, grantData?.id);

            if (canStillViewStep) {
                // User can still view - update selection and refresh
                taskListActions.setSelectedStep(step);
                await taskListActions.refreshSteps(step);
            } else {
                // User will lose view permission - clear selection first, then refresh
                // Clear selections to trigger grid deselection via registered handlers
                taskListActions.clearSelections();

                // Explicitly set step to null
                taskListActions.setSelectedStep(null);

                // Refresh steps (this will filter out the step the user can't see)
                await taskListActions.refreshSteps(step);

                // Check if user still has View permission for the phase
                // If so, select it; otherwise selections remain cleared
                if (selectedPhase) {
                    const canStillViewPhase = checkPhasePermission(selectedPhase, app, PermissionType.View, false, grantData?.id);
                    if (canStillViewPhase) {
                        taskListActions.setSelectedPhase(selectedPhase);
                    }
                }
            }

            hideTaskListLoadPanel();
        },
        [taskListActions, showTaskListLoadPanel, hideTaskListLoadPanel, app, selectedPhase, grantData]
    );

    const onClickClose = useCallback(() => {
        taskListActions.clearSelections();
        taskListActions.setSelectedPhase(null);
        taskListActions.setSelectedStep(null);
    }, [taskListActions]);

    // Complete action for None workflow - sets completion date to today if not set, then saves
    // The backend auto-transitions to APPROVED when completion date is set for None workflow
    // and handles sending notifications
    const onClickComplete = useCallback(async () => {
        if (!selectedStep || workflowType !== PhaseWorkflowType.None) return;

        showTaskListLoadPanel();

        // Set start date (submissionDate) and completion date to today if not already set
        // Backend auto-transitions to APPROVED when completion date is set for None workflow
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const updatedStep = {
            ...trimStepText(selectedStep),
            submissionDate: selectedStep.submissionDate || today,
            completionDate: selectedStep.completionDate || today
        };

        // Save the step with completion date - backend auto-transitions to APPROVED with None workflow
        let step: StepDto | null = null;

        if (mode === "templates") {
            const defaultStepDto = mapStepDtoToDefaultStep(updatedStep);

            if (defaultStepDto.id != 0) {
                const result = await updateDefaultSubtask(selectedPhase?.defaultPhase.moduleTypeId as number, defaultStepDto);
                if (result?.error) app.errorToast(result.error.message);
                step = mapDefaultStepToStepDto(result?.data as DefaultStepDto) ?? null;
            } else {
                const result = await createDefaultSubtask(selectedPhase?.defaultPhase.moduleTypeId as number, defaultStepDto);
                if (result?.error) app.errorToast(result.error.message);
                step = mapDefaultStepToStepDto(result?.data as DefaultStepDto) ?? null;
            }
        } else {
            if (updatedStep.id) {
                const result = await updateStep(updatedStep.id, updatedStep);
                if (result?.error) app.errorToast(result.error.message);
                step = (result?.data as StepDto) ?? null;
            } else {
                const result = await createStep(updatedStep);
                if (result?.error) app.errorToast(result.error.message);
                step = (result?.data as StepDto) ?? null;
            }
        }

        if (step) await refreshStepChanges(step);
        hideTaskListLoadPanel();
    }, [selectedStep, selectedPhase, workflowType, mode, app, showTaskListLoadPanel, hideTaskListLoadPanel, refreshStepChanges]);

    const nameDescriptionDisabled = useCallback(() => {
        if (cycleClosedRef.current) return true;
        if (inTemplateMode)
            return !flags.allowEditName;

        const step = selectedStep;
        if (step) {
            // For new subtasks (no ID yet), allow editing
            if (step.id == null) return false;

            // For None workflow (Bidding, Construction), use permission-based editing
            if (workflowType === PhaseWorkflowType.None) {
                // Always disable for INACTIVE (no permissions configured for this status)
                if (step.status === StepStatus.INACTIVE)
                    return true;
                // Otherwise check permission config (controls which statuses allow editing)
                return !hasEditPermission;
            }

            // Standard workflow status-based logic
            if (
                step.status === StepStatus.GRANTEE_PENDING ||
                step.status === StepStatus.GRANTEE_COMPLETE ||
                step.status === StepStatus.GRANTEE_PENDING_REVISION ||
                step.status === StepStatus.PENDING ||
                step.status === StepStatus.APPROVED ||
                step.status === StepStatus.INACTIVE
            )
                return true;

            if (step.id == null) return false;

            // Use config-based permission check (EditRequest for Phase/Step workflows)
            return !hasEditRequestPermission;
        }
        return true;
    }, [flags, selectedStep, workflowType, hasEditRequestPermission]);

    const dueDateDisabled = useCallback(() => {
        if (cycleClosedRef.current) return true;

        if (inTemplateMode)
            return !flags.allowEditDueDate;

        const step = selectedStep;
        if (step) {
            // For None workflow (Bidding, Construction), use permission-based editing
            if (workflowType === PhaseWorkflowType.None) {
                // Always disable for INACTIVE (no permissions configured for this status)
                if (step.status === StepStatus.INACTIVE)
                    return true;
                // Otherwise check permission config (controls which statuses allow editing)
                return !hasEditDatesPermission;
            }

            // Standard workflow status-based logic
            if (
                step.status === StepStatus.GRANTEE_PENDING ||
                step.status === StepStatus.GRANTEE_COMPLETE ||
                step.status === StepStatus.PENDING ||
                step.status === StepStatus.GRANTEE_PENDING_REVISION ||
                step.status === StepStatus.APPROVED ||
                step.status === StepStatus.INACTIVE
            )
                return true;

            if (step.id == null) return false;

            // Use config-based permission check
            return !hasEditDatesPermission;
        }
        return true;
    }, [flags, selectedStep, workflowType, hasEditDatesPermission]);

    const trackingDatesDisabled = useCallback(() => {
        if (cycleClosedRef.current) return true;
        const step = selectedStep;
        if (step) {
            // For new subtasks (no ID yet), allow editing tracking dates
            if (step.id == null) return false;

            // Config-driven: the EditDates permission for the step's current status
            // governs editability for every workflow type (matches dueDateDisabled and
            // the backend gate). No hardcoded per-status or per-workflow assumptions.
            return !hasEditDatesPermission;
        }
        return false;
    }, [selectedStep, hasEditDatesPermission]);

    const uploadDisabled = useCallback(() => {
        if (inTemplateMode) return !flags.allowEditName;

        const step = selectedStep;
        if (!step) return true;

        // For None workflow, use permission-based upload control
        if (workflowType === PhaseWorkflowType.None) {
            // Always disable for INACTIVE (no permissions configured for this status)
            if (step.status === StepStatus.INACTIVE) return true;
            // Otherwise check permission config (controls which statuses allow uploading)
            return !hasUploadPermission;
        }

        // For Phase/Step workflows, check Upload permission directly
        return !hasUploadPermission;
    }, [inTemplateMode, flags, selectedStep, workflowType, hasUploadPermission]);

    // Request upload requires BOTH EditRequest (primary) AND Upload (secondary) permissions
    const requestUploadDisabled = useCallback(() => {
        if (inTemplateMode) return !flags.allowEditName;

        const step = selectedStep;
        if (!step) return true;

        // Must have EditRequest permission (primary) AND Upload permission (secondary)
        return !hasEditRequestPermission || !hasUploadPermission;
    }, [inTemplateMode, flags, selectedStep, hasEditRequestPermission, hasUploadPermission]);

    // Response upload requires BOTH EditResponse (primary) AND Upload (secondary) permissions
    const responseUploadDisabled = useCallback(() => {
        if (inTemplateMode) return !flags.allowEditName;

        const step = selectedStep;
        if (!step) return true;

        // Must have EditResponse permission (primary) AND Upload permission (secondary)
        return !hasEditResponsePermission || !hasUploadPermission;
    }, [inTemplateMode, flags, selectedStep, hasEditResponsePermission, hasUploadPermission]);

    // Find previous and next steps for date constraints
    const { prevStep, nextStep } = useMemo(() => {
        if (!selectedStep || !isNoOverlapMode || siblingSteps.length === 0) {
            return { prevStep: null, nextStep: null };
        }
        const currentIndex = siblingSteps.findIndex(s => s.id === selectedStep.id);
        if (currentIndex === -1) {
            return { prevStep: null, nextStep: null };
        }
        return {
            prevStep: currentIndex > 0 ? siblingSteps[currentIndex - 1] : null,
            nextStep: currentIndex < siblingSteps.length - 1 ? siblingSteps[currentIndex + 1] : null
        };
    }, [selectedStep?.id, siblingSteps, isNoOverlapMode]);

    // Date options for Projected Start (startDate field)
    const projectedStartDateOptions = useMemo(() => {
        const options: { displayFormat: string; readOnly: boolean; min?: Date; max?: Date } = {
            displayFormat: "shortDate",
            readOnly: trackingDatesDisabled()
        };

        if (workflowType === PhaseWorkflowType.None && !trackingDatesDisabled()) {
            // Min: previous step's projectedEndDate, or phase start
            if (isNoOverlapMode && prevStep?.projectedEndDate) {
                options.min = new Date(prevStep.projectedEndDate);
            } else if (selectedPhase?.startDate) {
                options.min = new Date(selectedPhase.startDate);
            }

            // Max: this step's projectedEndDate, or phase end
            if (selectedStep?.projectedEndDate) {
                options.max = new Date(selectedStep.projectedEndDate);
            } else if (selectedPhase?.projectedEndDate) {
                options.max = new Date(selectedPhase.projectedEndDate);
            }
        }

        return options;
    }, [isCycleClosed, trackingDatesDisabled, workflowType, selectedPhase?.startDate, selectedPhase?.projectedEndDate,
        isNoOverlapMode, prevStep?.projectedEndDate, selectedStep?.projectedEndDate]);

    // Date options for Projected Completion (projectedEndDate field)
    const projectedCompletionDateOptions = useMemo(() => {
        const options: { displayFormat: string; readOnly: boolean; min?: Date; max?: Date } = {
            displayFormat: "shortDate",
            readOnly: trackingDatesDisabled()
        };

        if (workflowType === PhaseWorkflowType.None && !trackingDatesDisabled()) {
            // EndDateOnly mode: bounds are simply the phase start and end dates
            // The cascade logic handles re-sorting automatically
            if (isEndDateOnlyMode) {
                if (selectedPhase?.startDate) {
                    options.min = new Date(selectedPhase.startDate);
                }
                if (selectedPhase?.projectedEndDate) {
                    options.max = new Date(selectedPhase.projectedEndDate);
                }
            } else {
                // Manual/NoOverlap mode: per-step constraints
                // Min: this step's startDate, or phase start
                if (selectedStep?.startDate) {
                    options.min = new Date(selectedStep.startDate);
                } else if (selectedPhase?.startDate) {
                    options.min = new Date(selectedPhase.startDate);
                }

                // Max: next step's startDate, or phase end
                if (isNoOverlapMode && nextStep?.startDate) {
                    options.max = new Date(nextStep.startDate);
                } else if (selectedPhase?.projectedEndDate) {
                    options.max = new Date(selectedPhase.projectedEndDate);
                }
            }
        }

        return options;
    }, [isCycleClosed, trackingDatesDisabled, workflowType, selectedPhase?.startDate, selectedPhase?.projectedEndDate,
        isNoOverlapMode, nextStep?.startDate, selectedStep?.startDate, isEndDateOnlyMode]);

    // Date options for Start Date (submissionDate field) - max is completion date (real-time)
    const startDateOptions = useMemo(() => {
        const options: { displayFormat: string; readOnly: boolean; min?: Date; max?: Date } = {
            displayFormat: "shortDate",
            readOnly: trackingDatesDisabled()
        };

        if (workflowType === PhaseWorkflowType.None && !trackingDatesDisabled()) {
            // Max is the completion date (can't start after completion)
            if (currentCompletionDate) {
                options.max = currentCompletionDate;
            }
        }

        return options;
    }, [isCycleClosed, trackingDatesDisabled, workflowType, currentCompletionDate]);

    const requestEditDisabled = useCallback(() => {
        if (cycleClosedRef.current) return true;

        if (inTemplateMode)
            return !flags.allowEditRequest;

        const step = selectedStep;
        if (step) {
            // For new tasks (no ID), allow if user has EditRequest permission
            if (step.id == null) {
                return !hasEditRequestPermission;
            }

            // Request section is hidden for None workflow, so always disable
            if (workflowType === PhaseWorkflowType.None) return true;

            // Use config-based permission check
            return !hasEditRequestPermission;
        }
        return true;
    }, [flags, selectedStep, selectedPhase, workflowType, hasEditRequestPermission]);

    const requestIsVisible = useCallback(() => {
        // Hide request section for the None workflow (no request/response cycle - files only)
        if (workflowType === PhaseWorkflowType.None) return false;

        // In template mode, use flags
        if (inTemplateMode) return flags.showRequestSection;

        const step = selectedStep;
        if (step) {
            if (step.status === StepStatus.INACTIVE) return false;

            // Check View permission from config
            if (!hasViewPermission) return false;

            // Check if there's any request content (comment or files)
            const hasRequestComment = step.requestComment?.comment &&
                step.requestComment.comment.replace(/<[^>]+>/g, "").trim() !== "";
            const hasRequestFiles = step.requestComment?.files &&
                step.requestComment.files.length > 0;
            const hasContent = hasRequestComment || hasRequestFiles;

            // Show if user can edit request (config-based)
            if (hasEditRequestPermission) return true;

            // Show if there's content (read-only view for others)
            if (hasContent) return true;
        }
        return false;
    }, [flags, selectedStep, hasEditRequestPermission, hasViewPermission, workflowType, inTemplateMode]);

    const responseEditDisabled = useCallback(() => {
        if (cycleClosedRef.current) return true;

        if (inTemplateMode)
            return !flags.allowEditResponse;

        const step = selectedStep;
        if (step) {
            if (step.id == null) return true;

            // Use config-based permission check
            return !hasEditResponsePermission;
        }
        return true;
    }, [flags, selectedStep, hasEditResponsePermission]);

    const responseIsVisible = useCallback(() => {
        // Hide response section for the None workflow (no request/response cycle - files only)
        if (workflowType === PhaseWorkflowType.None) return false;

        if (inTemplateMode)
            return flags.showResponseSection;

        const step = selectedStep;
        if (step) {
            if (step.status === StepStatus.INACTIVE) return false;

            // System view rule: Response is only visible at GRANTEE_PENDING+ for all users
            if (!canViewResponseContent(step.status)) return false;

            // Show if user has View permission (will be read-only if no EditResponse)
            if (hasViewPermission) return true;

            // Show if user can edit response (config-based)
            if (hasEditResponsePermission) return true;

            // Check if there's any response content (comment or files)
            const hasResponseComment = step.responseComment?.comment &&
                step.responseComment.comment.replace(/<[^>]+>/g, "").trim() !== "";
            const hasResponseFiles = step.responseComment?.files &&
                step.responseComment.files.length > 0;
            const hasContent = hasResponseComment || hasResponseFiles;

            // Show if there's content (read-only view for others)
            if (hasContent) return true;
        }
        return false;
    }, [flags, selectedStep, hasEditResponsePermission, hasViewPermission, workflowType, inTemplateMode]);

    const commentsAreVisible = useCallback(() => {

        if (inTemplateMode)
            return flags.showComments;

        const step = selectedStep;
        if (!step) return false;
        if (step.status === StepStatus.INACTIVE) return false;
        if (!hasViewPermission) return false;

        // None workflow has no request/response sections, but comments are still allowed
        if (workflowType === PhaseWorkflowType.None) return true;

        return requestIsVisible();

    }, [flags, selectedStep, hasViewPermission, workflowType, inTemplateMode, requestIsVisible]);

    const nameOptions = useMemo(
        () => ({
            readOnly: nameDescriptionDisabled(),
            maxLength: 50,
        }),
        [isCycleClosed, nameDescriptionDisabled]
    );

    const dateOptions = useMemo(
        () => ({
            displayFormat: "shortDate",
            // For None workflow, min is the start date (can't complete before starting) - real-time
            min: workflowType === PhaseWorkflowType.None
                ? (currentSubmissionDate || undefined)
                : new Date(),
            readOnly: dueDateDisabled(),
        }),
        [isCycleClosed, dueDateDisabled, workflowType, currentSubmissionDate]
    );

    // Dynamic dataField and label based on workflow type
    const dateFieldName = useMemo(
        () => workflowType === PhaseWorkflowType.None ? "completionDate" : "dueDate",
        [workflowType]
    );

    const dateFieldLabel = useMemo(
        () => workflowType === PhaseWorkflowType.None ? "Completion Date" : "Due Date",
        [workflowType]
    );

    const getPlainText = useCallback((html: string | undefined | null) => {
        return (html ?? "").replace(/<[^>]+>/g, "").trim();
    }, []);

    const handleHtmlRequestChange = useCallback(
        (val: string) => {
            htmlEditorText.current = val;
            const plainText = getPlainText(val);
            readyDisabled.current = plainText.length === 0;
        },
        [getPlainText]
    );

    const handleHtmlResponseChange = useCallback(
        (val: string) => {
            htmlEditorText.current = val;
            const plainText = getPlainText(val);
            completeDisabled.current = plainText.length === 0;
        },
        [getPlainText]
    );

    const handleDescriptionChange = useCallback(
        (val: string) => {
            const text = (val ?? "").trim();
            completeDisabled.current = text.length === 0;
        },
        []
    );

    const headerDueDateText = useCallback(() => {
        // None workflow doesn't use due dates - it uses completion dates instead
        if (workflowType === PhaseWorkflowType.None) {
            return null;
        }
        if (selectedStep?.dueDate?.toDateString() != null) {
            return `Due: ${selectedStep.dueDate?.toDateString()}`;
        }
        return null;
    }, [selectedStep, workflowType]);

    const getFormattedDate = useCallback((input: Date | string | null | undefined): string => {
        if (!input) return "";
        try {
            const date = input instanceof Date ? input : new Date(input);
            if (isNaN(date.getTime())) {
                console.error("Invalid date:", input);
                return "";
            }
            // Format in the View with date-fns (kept here as plain date -> string)
            return date.toISOString();
        } catch (err) {
            console.error("Error formatting date:", err);
            return "";
        }
    }, []);

    const requestCommentDate = useCallback(() => {
        const d = selectedStep?.requestComment?.dateUpdated?.toDateString() != null
            ? selectedStep?.requestComment?.dateUpdated
            : null;
        return d ? getFormattedDate(d) : "";
    }, [selectedStep, getFormattedDate]);

    const responseCommentDate = useCallback(() => {
        const d = selectedStep?.responseComment?.dateUpdated?.toDateString() != null
            ? selectedStep?.responseComment?.dateUpdated
            : null;
        return d ? getFormattedDate(d) : "";
    }, [selectedStep, getFormattedDate]);

    const handleDeleteClick = async () => {
        let result = await confirm(`Are you sure you want to delete subtask<br /><strong>${selectedStep?.name}</strong>?`, "Confirm deletion");

        if (result) {
            await onStepDeletion();
        }
    };

    const onStepDeletion = useCallback(async () => {
        if (selectedStep) {
            const result = await deleteStep(selectedStep.id);
            if (result?.error) {
                app.errorToast(result.error.message);
            } else if (result?.data === true) {
                // Clear the selected subtask and select the parent task
                taskListActions.setSelectedStep(null);
                taskListActions.setSelectedPhase(selectedPhase);
                await taskListActions.refreshSteps();
                app.infoToast("Subtask deleted successfully.");
            }
        }
    }, [selectedStep, selectedPhase, app, taskListActions]);

    const deleteButtonDisabled = useCallback(() => {
        if (cycleClosedRef.current) return true;
        const step = selectedStep;
        if (step && selectedPhase) {
            // Use config-based permission check
            return !hasDeletePermission;
        }
        return true;
    }, [selectedStep, selectedPhase, hasDeletePermission]);

    const handleTaskLoading = useCallback(() => {
        setLoadingTasks((c) => c + 1);
    }, []);

    const handleTaskLoaded = useCallback(() => {
        setLoadingTasks((c) => c - 1);
    }, []);

    // Phase summary toggles
    const showPhaseSummary = useCallback(async (phase: PhaseDto | null) => {
        if (!phase?.id || mode === "templates") {
            // For templates or null phase, use the passed phase directly
            setSelectedSummaryPhase(phase);
            setShowSummary(true);
            return;
        }

        // For runtime phases, fetch fresh data from API to get calculated phaseActions
        const refreshedPhase = await getPhaseById(phase.id);
        if (refreshedPhase?.data) {
            setSelectedSummaryPhase(refreshedPhase.data);
        } else {
            // Fallback to passed phase if API call fails
            setSelectedSummaryPhase(phase);
        }
        setShowSummary(true);
    }, [mode]);

    const hidePhaseSummary = useCallback(() => {
        setSelectedSummaryPhase(null);
        setShowSummary(false);
    }, []);

    const showPhaseRequestResponseSummary = useCallback(async (phase: PhaseDto | null) => {
        if (!phase?.id || mode === "templates") {
            // For templates or null phase, use the passed phase directly
            setSelectedSummaryPhase(phase ? { ...phase } : null);
            setShowSummary(true);
            return;
        }

        // For runtime phases, fetch fresh data from API to get calculated phaseActions
        const refreshedPhase = await getPhaseById(phase.id);
        if (refreshedPhase?.data) {
            setSelectedSummaryPhase(refreshedPhase.data);
        } else {
            // Fallback to passed phase if API call fails
            setSelectedSummaryPhase(phase ? { ...phase } : null);
        }
        setShowSummary(true);
    }, [setSelectedSummaryPhase, setShowSummary, mode]);

    const hidePhaseRequestResponseSummary = useCallback(() => {
        setSelectedSummaryPhase(null);
        setShowSummary(false);
    }, [setSelectedSummaryPhase, setShowSummary]);

    // A caller outside the tasklist (the extended phase header) asked to open a phase summary.
    // Keyed on the token so clicking the same name again reopens it, and so a tasklist that mounts
    // with a request already pending (the header expanded a collapsed task) still opens it.
    const requestedSummaryToken = requestedSummaryPhase?.token ?? 0;
    const requestedSummaryPhaseRef = useRef<PhaseDto | null>(null);
    requestedSummaryPhaseRef.current = requestedSummaryPhase?.phase ?? null;

    // Read through refs so the token is the only trigger. taskListActions takes a new identity
    // every time a step is selected, so depending on it here re-opened the summary the moment the
    // user clicked a subtask - which cleared the step, changed the actions again, and looped.
    const taskListActionsRef = useRef(taskListActions);
    taskListActionsRef.current = taskListActions;
    const showPhaseRequestResponseSummaryRef = useRef(showPhaseRequestResponseSummary);
    showPhaseRequestResponseSummaryRef.current = showPhaseRequestResponseSummary;

    useEffect(() => {
        if (!requestedSummaryToken) return;
        const phase = requestedSummaryPhaseRef.current;
        if (!phase) return;

        // Match the tasklist's own name click: drop any subtask selection, then open the summary.
        const actions = taskListActionsRef.current;
        actions.clearSelections();
        actions.setSelectedStep(null);
        actions.setSelectedPhase(phase);
        void showPhaseRequestResponseSummaryRef.current(phase);
    }, [requestedSummaryToken]);

    // Ad-hoc subtask workflow type picker — shown only for new subtasks (no id) under a
    // hybrid parent task. Once saved the type is fixed (the backend persists it via the
    // synthesized DefaultStep), and the readonly badge near the name carries it forward.
    const isAdhocHybridNew = !inTemplateMode && isHybridParent && selectedStep != null && !selectedStep.id;
    const isStepHybridParent = parentWorkflowType === PhaseWorkflowType.StepHybrid;
    const subtaskWorkflowOptions = useMemo(() => isStepHybridParent
        ? [
            { id: StepWorkflowType.Step, name: 'Step — runs its own workflow' },
            { id: StepWorkflowType.None, name: 'None — simple complete/not-complete tracker' }
        ]
        : [
            { id: StepWorkflowType.Phase, name: 'Phase — drives the parent task\'s status' },
            { id: StepWorkflowType.Step, name: 'Step — runs its own workflow' },
            { id: StepWorkflowType.None, name: 'None — simple complete/not-complete tracker' }
        ], [isStepHybridParent]);

    const adhocSubtaskWorkflowType: StepWorkflowType | null =
        selectedStep?.defaultStep?.settings?.workflowType ?? null;

    const setAdhocSubtaskWorkflowType = useCallback((value: StepWorkflowType | null) => {
        if (!selectedStep) return;
        const next: StepDto = {
            ...selectedStep,
            defaultStep: {
                ...(selectedStep.defaultStep ?? { id: 0 } as any),
                settings: {
                    ...((selectedStep.defaultStep?.settings ?? {}) as any),
                    workflowType: value
                }
            } as any
        };
        taskListActions.setSelectedStep(next);
    }, [selectedStep, taskListActions]);

    const getStepName = useCallback(() => {
        if (selectedStep != null) {
            if (mode === "templates")
                return selectedStep?.name;
            else {
                const newAdder = selectedStep?.id ? "" : "(New Subtask) ";
                const stepName = selectedStep.name ? " - " + selectedStep.name : "";
                // Upload-only subtasks are file containers with no step status.
                const stepStatus = selectedStep.id && !uploadOnly
                    ? " (" + getStepStatusInfoByWorkflow(workflowType, selectedStep).text + ") "
                    : ""

                return newAdder + selectedPhase?.name + stepName + stepStatus;
            }
        }

        return null;
    }, [selectedStep, selectedPhase, mode, workflowType]);

    return {
        // registration from View
        registerLoadPanel,

        // values referenced in View
        xsrf,
        showSummary,
        selectedSummaryPhase,
        initialLoad,

        readyDisabled,
        completeDisabled,

        htmlEditorExpanded,
        htmlEditorText,
        htmlEditorTarget,

        // callbacks used by View
        setHtmlEditorExpanded,
        onClickSave,
        onClickComplete,
        onClickClose,
        onFieldDataChanged,

        nameDescriptionDisabled,
        dueDateDisabled,
        trackingDatesDisabled,
        projectedStartDateOptions,
        projectedCompletionDateOptions,
        startDateOptions,
        uploadDisabled,
        requestUploadDisabled,
        responseUploadDisabled,
        requestEditDisabled,
        requestIsVisible,
        responseEditDisabled,
        responseIsVisible,
        commentsAreVisible,
        canEditAnything,
        isCycleClosed,

        nameOptions,
        dateOptions,
        dateFieldName,
        dateFieldLabel,

        handleHtmlRequestChange,
        handleHtmlResponseChange,
        handleDescriptionChange,

        headerDueDateText,
        requestCommentDate,
        responseCommentDate,

        handleDeleteClick,
        deleteButtonDisabled,

        handleTaskLoading,
        handleTaskLoaded,
        handleTaskVisibilityChange,
        hasAnyVisibleTasks,
        handleTaskCollapsedChange,
        everyTaskCollapsed,

        showPhaseSummary,
        hidePhaseSummary,
        showPhaseRequestResponseSummary,
        hidePhaseRequestResponseSummary,

        // pass-through props
        grantData,
        selectedStep: selectedStep ?? null,
        selectedPhase: selectedPhase ?? null,
        phasesInfo,
        deepLinkHash,
        nameShownExternally,
        externalPhase,

        getStepName,

        mode,
        flags,
        workflowType,
        uploadOnly,

        // Ad-hoc subtask workflow type picker (hybrid instance only)
        isAdhocHybridNew,
        subtaskWorkflowOptions,
        adhocSubtaskWorkflowType,
        setAdhocSubtaskWorkflowType,
        // Reload steps/phase after a phase-level action (e.g. starting a resubmission).
        refreshSteps: taskListActions.refreshSteps,

        // Resubmission cycle navigation.
        selectedSubmissionId,
    };
}
